/**
 * The text rule of the detector, ported from `src/extract_features.py`.
 *
 * The Python side is two functions. `fold_accents` decomposes with NFKD and drops every combining
 * mark. `letters_only` then drops every character that is not a letter.
 *
 * ## The second step subsumes the first
 *
 * A combining mark is never a letter, so the `isalpha` filter of step 2 would remove any mark that
 * step 1 left behind. The two steps therefore compose to one rule:
 *
 *     normalise with NFKD, then keep the letters
 *
 * `test/normalise.test.ts` checks that claim against the Python output rather than trusting it.
 *
 * ## Why `\p{L}` is the right test
 *
 * Python's `str.isalpha` is true for the Unicode categories Lu, Ll, Lt, Lm, and Lo. The JavaScript
 * property escape `\p{L}` matches exactly those five categories. So the two agree character for
 * character, and neither is an `A-Za-z` rule.
 *
 * ## Why the code iterates code points
 *
 * A JavaScript string indexes UTF-16 units. Python indexes code points. A letter outside the basic
 * plane is two units in JavaScript and one in Python, so an index loop would cut a different
 * 5-gram on such a text. `Array.from` iterates code points, and that removes the difference.
 */

import type { Variant } from './types';

const LETTER = /\p{L}/u;

/**
 * Return the letters of the text, and nothing else.
 *
 * The result is one unbroken run of letters. No space survives, because a space is not a letter.
 */
export function lettersOnly(text: string): string {
  const decomposed = text.normalize('NFKD');
  let out = '';
  for (const character of decomposed) {
    if (LETTER.test(character)) {
      out += character;
    }
  }
  return out;
}

/**
 * Return how many letters the text holds under the rule above.
 *
 * The count reads code points, so an astral letter counts once rather than twice. The refusal
 * floor of the detector reads this number.
 */
export function letterCount(text: string): number {
  return Array.from(lettersOnly(text)).length;
}


/**
 * Return the letters of the text, folded to lower case.
 *
 * This is `lettersOnly` and then one more step, and it ports `letters_lower` of
 * `src/extract_features.py`. **The case fold runs after the letters are chosen, and not before.**
 * `String.prototype.toLowerCase` can change the length of a string, and a length change before
 * the letters are chosen would move an n-gram boundary.
 */
export function lettersLower(text: string): string {
  return lettersOnly(text).toLowerCase();
}

/**
 * Return the text under one variant.
 *
 * **The variant is a property of the model, not of the page.** Item 35 shipped a `letters_lower`
 * detector beside a `letters_only` one, so the caller must read `Detector.meta.variant`.
 */
export function applyVariant(text: string, variant: Variant): string {
  return variant === 'letters_lower' ? lettersLower(text) : lettersOnly(text);
}
